/**
 * crossTabDrag — codec for the ⌥Alt+drag NATIVE drag-and-drop payload that
 * carries a grid item between two tabs editing different sites (the item is
 * COPIED into the destination site; see docs/plans/2026-07-17-cross-tab-drag.md).
 *
 * Two DataTransfer entries travel together:
 *
 * 1. `application/x-wb-item` — the full JSON payload (schema below). Browsers
 *    expose its DATA only at `drop` (protected mode), so it drives the commit.
 * 2. `application/x-wb-item-meta;kind=app;w=6;h=2;ct=text` — a mini-spec
 *    encoded IN THE TYPE STRING itself, because `dataTransfer.types` is the
 *    only thing readable during `dragover` and the landing preview
 *    (`startExternalAdd`) needs kind/size/contentType before the drop.
 *    Browsers lowercase type strings, so the whole grammar is lowercase-safe.
 *
 * IDs never travel — the receiver mints fresh ones (it's a copy). Asset BYTES
 * never travel either — only content addresses; both tabs share the origin's
 * OPFS, so the drop side copies `sites/<sourceSiteId>/_assets/<sha>` into its
 * own bucket (with a backend fetch fallback).
 */
import type { GridItem } from "@fredericrous/grid";

import { lexicalStateToRuns } from "./widgets/lexicalRuns.js";

export const WB_ITEM_TYPE = "application/x-wb-item";
export const WB_META_PREFIX = "application/x-wb-item-meta;";

const PAYLOAD_MAGIC = "wb-item";
const PAYLOAD_VERSION = 1;
/** Pathological lexical states aside, payloads are tiny (assets are refs).
 *  Anything past this is malformed or hostile — refuse the dragstart. */
export const MAX_PAYLOAD_BYTES = 256 * 1024;

/** Content-address of an asset referenced by the item (never the bytes). */
export interface CrossTabAssetRef {
  sha256: string;
  ext: string;
  mime: string;
}

export interface CrossTabPayload {
  v: number;
  magic: typeof PAYLOAD_MAGIC;
  /** Guards against foreign apps using the same MIME type; P1 accepts only
   *  same-origin payloads (shared OPFS is what makes assets work anyway). */
  origin: string;
  /** Source SITE (draft) id — where the drop side reads asset bytes from. */
  sourceSiteId: string;
  kind: "app" | "container";
  item: {
    contentType: string;
    contentProps: Record<string, unknown>;
    size: { w: number; h: number };
    /** On-canvas LAYOUT size (px, pre-zoom) — lets the receiver refit the
     *  unit size against ITS grid geometry (source units mean different
     *  pixels in a differently-sized grid). Optional: old payloads and
     *  non-measurable sources fall back to unit-for-unit. */
    pxSize?: { w: number; h: number };
  };
  assets: CrossTabAssetRef[];
}

/** Mini-spec readable during `dragover` (rides the meta TYPE string). */
export interface CrossTabMeta {
  kind: "app" | "container";
  w: number;
  h: number;
  ct: string;
  /** Layout pixel size (optional; drives the receiver's px-true refit). */
  pw?: number;
  ph?: number;
}

// Type strings are lowercased by browsers; keep tokens lowercase-safe and
// refuse anything that wouldn't survive the round-trip.
const CT_TOKEN = /^[a-z0-9_-]{1,32}$/;

/** Items that may leave the tab. Containers (sections) are P2 — their nested
 *  subtree needs a serialized-insert path that doesn't exist yet. Pinned
 *  shared-region defaults (`__` ids) never leave their band, so they never
 *  leave the tab either. */
export function isCrossTabDraggable(item: {
  id: string;
  contentType: string;
}): boolean {
  return !item.id.startsWith("__") && item.contentType !== "grid";
}

/** Collect the item's asset content-addresses (image widgets today; any
 *  contentProps carrying a `sha256` follows the same convention). */
function assetRefsOf(props: Record<string, unknown>): CrossTabAssetRef[] {
  const sha256 = props["sha256"];
  if (typeof sha256 !== "string" || sha256.length === 0) return [];
  return [
    {
      sha256,
      ext: typeof props["ext"] === "string" ? (props["ext"] as string) : "bin",
      mime:
        typeof props["mime"] === "string"
          ? (props["mime"] as string)
          : "application/octet-stream",
    },
  ];
}

/** The full drag payload for one grid item, or null when it may not travel
 *  (container, pinned default, non-serializable props). */
export function buildCrossTabPayload(
  item: GridItem,
  sourceSiteId: string,
  origin: string,
  pxSize?: { w: number; h: number },
): CrossTabPayload | null {
  if (!isCrossTabDraggable(item)) return null;
  if (!CT_TOKEN.test(item.contentType)) return null;
  let contentProps: Record<string, unknown>;
  try {
    // JSON round-trip: proves serializability and drops any live references.
    contentProps = JSON.parse(
      JSON.stringify(item.contentProps ?? {}),
    ) as Record<string, unknown>;
  } catch {
    return null;
  }
  return {
    v: PAYLOAD_VERSION,
    magic: PAYLOAD_MAGIC,
    origin,
    sourceSiteId,
    kind: "app",
    item: {
      contentType: item.contentType,
      contentProps,
      size: { w: item.position.w, h: item.position.h },
      ...(pxSize && pxSize.w > 0 && pxSize.h > 0
        ? { pxSize: { w: Math.round(pxSize.w), h: Math.round(pxSize.h) } }
        : {}),
    },
    assets: assetRefsOf(contentProps),
  };
}

/** Serialize for `setData` — null when the payload exceeds the size cap. */
export function encodePayload(payload: CrossTabPayload): string | null {
  const json = JSON.stringify(payload);
  if (json.length > MAX_PAYLOAD_BYTES) return null;
  return json;
}

/** The meta TYPE STRING for a payload (dragover-readable mini-spec). */
export function encodeMetaType(payload: CrossTabPayload): string {
  const { kind, item } = payload;
  const px = item.pxSize ? `;pw=${item.pxSize.w};ph=${item.pxSize.h}` : "";
  return `${WB_META_PREFIX}kind=${kind};w=${item.size.w};h=${item.size.h};ct=${item.contentType}${px}`;
}

/** Parse the mini-spec back out of `dataTransfer.types`. Null when this drag
 *  isn't ours or the grammar doesn't hold. */
export function parseMetaType(
  types: readonly string[] | undefined,
): CrossTabMeta | null {
  const t = types?.find((s) => s.toLowerCase().startsWith(WB_META_PREFIX));
  if (!t) return null;
  const fields = new Map<string, string>();
  for (const pair of t.slice(WB_META_PREFIX.length).split(";")) {
    const eq = pair.indexOf("=");
    if (eq <= 0) return null;
    fields.set(pair.slice(0, eq), pair.slice(eq + 1));
  }
  const kind = fields.get("kind");
  const w = Number(fields.get("w"));
  const h = Number(fields.get("h"));
  const ct = fields.get("ct") ?? "";
  if (kind !== "app" && kind !== "container") return null;
  if (!Number.isInteger(w) || w < 1 || w > 99) return null;
  if (!Number.isInteger(h) || h < 1 || h > 999) return null;
  if (!CT_TOKEN.test(ct)) return null;
  const meta: CrossTabMeta = { kind, w, h, ct };
  const pw = Number(fields.get("pw"));
  const ph = Number(fields.get("ph"));
  if (Number.isInteger(pw) && pw > 0 && Number.isInteger(ph) && ph > 0) {
    meta.pw = pw;
    meta.ph = ph;
  }
  return meta;
}

/** Strict receive-side validation. Null on ANY deviation — drops of foreign
 *  or damaged payloads are silently ignored. P1: same-origin only. */
export function parseCrossTabPayload(
  json: string,
  expectedOrigin: string,
): CrossTabPayload | null {
  if (typeof json !== "string" || json.length === 0) return null;
  if (json.length > MAX_PAYLOAD_BYTES) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (typeof raw !== "object" || raw === null) return null;
  const p = raw as Record<string, unknown>;
  if (p["v"] !== PAYLOAD_VERSION) return null;
  if (p["magic"] !== PAYLOAD_MAGIC) return null;
  if (p["origin"] !== expectedOrigin) return null;
  if (typeof p["sourceSiteId"] !== "string" || !p["sourceSiteId"]) return null;
  if (p["kind"] !== "app" && p["kind"] !== "container") return null;
  const item = p["item"] as Record<string, unknown> | undefined;
  if (typeof item !== "object" || item === null) return null;
  const ct = item["contentType"];
  if (typeof ct !== "string" || !CT_TOKEN.test(ct)) return null;
  const props = item["contentProps"];
  if (typeof props !== "object" || props === null || Array.isArray(props))
    return null;
  const size = item["size"] as Record<string, unknown> | undefined;
  if (typeof size !== "object" || size === null) return null;
  const w = size["w"];
  const h = size["h"];
  if (!Number.isInteger(w) || (w as number) < 1) return null;
  if (!Number.isInteger(h) || (h as number) < 1) return null;
  const pxRaw = item["pxSize"] as Record<string, unknown> | undefined;
  const pxSize =
    typeof pxRaw === "object" &&
    pxRaw !== null &&
    Number.isInteger(pxRaw["w"]) &&
    (pxRaw["w"] as number) > 0 &&
    Number.isInteger(pxRaw["h"]) &&
    (pxRaw["h"] as number) > 0
      ? { w: pxRaw["w"] as number, h: pxRaw["h"] as number }
      : undefined;
  const assetsRaw = p["assets"];
  if (!Array.isArray(assetsRaw)) return null;
  const assets: CrossTabAssetRef[] = [];
  for (const a of assetsRaw) {
    if (typeof a !== "object" || a === null) return null;
    const ar = a as Record<string, unknown>;
    if (typeof ar["sha256"] !== "string" || !ar["sha256"]) return null;
    assets.push({
      sha256: ar["sha256"] as string,
      ext: typeof ar["ext"] === "string" ? (ar["ext"] as string) : "bin",
      mime:
        typeof ar["mime"] === "string"
          ? (ar["mime"] as string)
          : "application/octet-stream",
    });
  }
  return {
    v: PAYLOAD_VERSION,
    magic: PAYLOAD_MAGIC,
    origin: p["origin"] as string,
    sourceSiteId: p["sourceSiteId"] as string,
    kind: p["kind"] as "app" | "container",
    item: {
      contentType: ct,
      contentProps: props as Record<string, unknown>,
      size: { w: w as number, h: h as number },
      ...(pxSize ? { pxSize } : {}),
    },
    assets,
  };
}

/** The drop's mini-spec and full payload must describe the SAME item — a
 *  mismatch means the transfer got crossed (or spoofed) and the add aborts. */
export function payloadMatchesMeta(
  payload: CrossTabPayload,
  meta: CrossTabMeta,
): boolean {
  return (
    payload.kind === meta.kind &&
    payload.item.size.w === meta.w &&
    payload.item.size.h === meta.h &&
    payload.item.contentType === meta.ct
  );
}

/** Free interop: a text widget dropped into any external editor pastes its
 *  plain text. Empty string when the item has none. */
export function textFallbackOf(props: Record<string, unknown>): string {
  const state = props["lexicalState"];
  if (typeof state !== "string" || !state) return "";
  try {
    return lexicalStateToRuns(state)
      .map((r) => r.text)
      .join("");
  } catch {
    return "";
  }
}
